import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, Typography } from '@material-ui/core';

import { useActivation } from '../ActivationContext';
import { useActivationBase } from '../hooks/useActivationBase';
import usePostAuthNavigation from '../hooks/usePostAuthNavigation';
import { usePostAuthRedirectTo } from '../hooks/usePostAuthRedirectTo';
import {
  ContentContainer, FlexContainer, HeaderBox, Title, continueButtonStyles,
} from './AuthMethodSelection.styles';

import sleepioTheme from '@/themes/sleepioTheme';
import Button from '@/components/Button';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { buildRedirectParam } from '@/utils/urlHelpers';
import { hasDeclinedPasswordPrompt, rememberPasswordPromptDeclined } from '@/utils/passwordAccount';

/**
 * "Want to set a password?" — asked once, after the emailed link has confirmed the
 * address, and only of a member who does not already have one.
 *
 * The member is **already signed in** by the time they see this, which is what makes
 * the whole thing cheap: saying yes goes straight to choosing a password, with no
 * email and no round-trip. That is why this replaced a field on the sign-up form —
 * a password set before an address is confirmed is a credential on an address the
 * member may never be able to reach.
 *
 * Saying no changes nothing at all, and is remembered for the rest of the session so
 * the question is not asked twice.
 */
const PasswordPromptInner: React.FC = () => {
  useAcceptedLanguage();

  const { t } = useTranslation();
  const history = useHistory();
  const base = useActivationBase();
  const { dispatch } = useActivation();
  // Resolved rather than read off the flow's state: a member who followed an emailed
  // link is in a fresh tab, where that state is empty. Losing it here would strand
  // them in a browser the app never hears back from.
  const redirectTo = usePostAuthRedirectTo();
  const navigatePostAuth = usePostAuthNavigation(redirectTo);
  const redirectParam = buildRedirectParam(redirectTo);

  useEffect(() => {
    dispatch({ type: 'SET_STEP', step: 'password_prompt' });
  }, [dispatch]);

  // Belt and braces on "asked once". The screen that routes here already checks this,
  // but landing here directly — a Back press, a bookmarked URL — would otherwise ask
  // again a member who has already said no.
  useEffect(() => {
    if (hasDeclinedPasswordPrompt()) {
      navigatePostAuth({ replace: true });
    }
  }, [navigatePostAuth]);

  const handleSetPassword = () => {
    dispatch({ type: 'SET_STEP', step: 'choose_password' });
    history.push(`${base}/password/new${redirectParam}`);
  };

  const handleNotNow = () => {
    rememberPasswordPromptDeclined();
    // Replace, not push. Pushing leaves this screen one Back press away, and going
    // back to it both re-asks a question already declined and re-runs the final step,
    // minting a fresh single-use hand-off code each time.
    navigatePostAuth({ replace: true });
  };

  return (
    <ContentContainer>
      <SleepioHeader />
      <FlexContainer maxWidth="xs">
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.password_prompt.title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.sleepio.activation.password_prompt.sub_title')}
          </Typography>
        </HeaderBox>

        <Button
          variant="contained"
          data-testid="set-password-button"
          type="button"
          fullWidth
          color="primary"
          rounded
          style={continueButtonStyles}
          onClick={handleSetPassword}
        >
          {t('coverage.pre_onboarding.sleepio.activation.password_prompt.set')}
        </Button>

        <Button
          variant="outlined"
          data-testid="not-now-button"
          type="button"
          fullWidth
          rounded
          style={continueButtonStyles}
          onClick={handleNotNow}
        >
          {t('coverage.pre_onboarding.sleepio.activation.password_prompt.not_now')}
        </Button>
      </FlexContainer>
      <Footer />
    </ContentContainer>
  );
};

export const PasswordPrompt: React.FC = () => (
  <ThemeProvider theme={sleepioTheme}>
    <PasswordPromptInner />
  </ThemeProvider>
);

export default PasswordPrompt;
